import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Plus, Search, UsersRound } from "lucide-react";
import type {
  UserDirectoryItem,
  UserDirectoryStatus,
  UserRole,
  CreatedUserResult,
} from "../../backend/api/users";
import { useCreateUser, useUserDirectory } from "../../backend/queries/users";
import { UserRoleBadge, UserAccountStatus as AccountStatus } from "../../components/domain-badges";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import TimezoneSelect from "../global/TimezoneSelect";
import { InvitationNotice } from "./InvitationNotice";

const roles: Array<"All" | UserRole> = ["All", "Owner", "Admin", "Agent", "Client"];
const statuses: Array<{ value: "all" | UserDirectoryStatus; label: string }> = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "disabled", label: "Disabled" },
];

type CreateForm = {
  firstName: string;
  lastName: string;
  email: string;
  contactNumber: string;
  timezone: string;
  role: "" | UserRole;
};

const emptyCreateForm = (): CreateForm => ({
  firstName: "",
  lastName: "",
  email: "",
  contactNumber: "",
  timezone: "",
  role: "",
});

const messageForError = (prefix: string, error: unknown) =>
  `${prefix}: ${error instanceof Error ? error.message : String(error)}`;

/**
 * The directory is a list only. Opening a user is a whole-row click through to
 * their own page, where editing, invitations and enable/disable live.
 */
const Users = () => {
  const navigate = useNavigate();
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm);
  const [roleFilter, setRoleFilter] = useState<"All" | UserRole>("All");
  const [statusFilter, setStatusFilter] = useState<"all" | UserDirectoryStatus>(
    // Everyone who has ever worked here now has a record, so the useful default
    // is who is here today rather than the full history.
    "active",
  );
  const [pageIndex, setPageIndex] = useState(0);
  const [cursorHistory, setCursorHistory] = useState<Array<string | null>>([
    null,
  ]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [formData, setFormData] = useState<CreateForm>(emptyCreateForm);
  const [createdUser, setCreatedUser] = useState<CreatedUserResult | null>(null);
  const pageSize = 10;

  const directoryQuery = useUserDirectory({
    search: debouncedSearch.trim() || undefined,
    role: roleFilter === "All" ? undefined : roleFilter,
    status: statusFilter === "all" ? undefined : statusFilter,
    cursor: cursorHistory[pageIndex] || undefined,
    limit: pageSize,
  });
  const users = directoryQuery.data?.items || [];
  const nextCursor = directoryQuery.data?.next_cursor || null;

  const createMutation = useCreateUser();

  useEffect(() => {
    if (
      pageIndex > 0 &&
      !directoryQuery.isFetching &&
      !directoryQuery.isPlaceholderData &&
      users.length === 0
    ) {
      setPageIndex((value) => Math.max(0, value - 1));
      setCursorHistory((history) => history.slice(0, -1));
    }
  }, [
    directoryQuery.isFetching,
    directoryQuery.isPlaceholderData,
    pageIndex,
    users.length,
  ]);

  const clearMessages = () => {
    setErrorMessage("");
    setSuccessMessage("");
  };

  const resetPaging = () => {
    setCursorHistory([null]);
    setPageIndex(0);
  };

  const openUser = (user: UserDirectoryItem) => navigate(`/users/${user.id}`);

  const handleCreateSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (!formData.role) {
      clearMessages();
      setErrorMessage("Choose a valid user role");
      return;
    }

    clearMessages();
    try {
      const created = await createMutation.mutateAsync({
        email: formData.email,
        firstname: formData.firstName,
        lastname: formData.lastName,
        contactnumber: formData.contactNumber,
        ianatimezonecode: formData.timezone,
        role: formData.role,
      });
      setCreatedUser(created);
      setShowCreatePopup(false);
      setFormData(emptyCreateForm());
      resetPaging();
    } catch (error) {
      setErrorMessage(messageForError("Could not create user", error));
    }
  };

  const visibleError = errorMessage || directoryQuery.error?.message;

  return (
    <div className="space-y-4">
      {(visibleError || successMessage) && (
        <MessageBanner
          error={visibleError}
          success={successMessage}
          onDismiss={clearMessages}
        />
      )}

      <Card>
        <CardHeader className="p-4 sm:p-5">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <UsersRound className="h-5 w-5" />
                Users
              </CardTitle>
              <CardDescription>
                {users.length} users on page {pageIndex + 1}
              </CardDescription>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-[minmax(220px,1fr)_150px_160px_auto] xl:w-[790px]">
              <div className="relative sm:col-span-2 xl:col-span-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value);
                    resetPaging();
                  }}
                  className="!pl-9"
                  placeholder="Search users..."
                  aria-label="Search users"
                />
              </div>
              <Select
                value={roleFilter}
                onValueChange={(value: "All" | UserRole) => {
                  setRoleFilter(value);
                  resetPaging();
                }}
              >
                <SelectTrigger aria-label="Filter by role">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={statusFilter}
                onValueChange={(value: "all" | UserDirectoryStatus) => {
                  setStatusFilter(value);
                  resetPaging();
                }}
              >
                <SelectTrigger aria-label="Filter by account status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={() => setShowCreatePopup(true)}>
                <Plus className="h-4 w-4" />
                Invite user
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-4 pt-0 sm:p-5 sm:pt-0">
          {directoryQuery.isFetching && (
            <div className="mb-3 text-sm text-muted-foreground" role="status">
              Updating user directory...
            </div>
          )}
          <div className="space-y-3 md:hidden">
            {users.length === 0 ? (
              <EmptyUsers
                searchTerm={searchTerm}
                loading={directoryQuery.isPending}
              />
            ) : (
              users.map((user) => (
                <div
                  key={user.id}
                  role="button"
                  tabIndex={0}
                  className="cursor-pointer rounded-md border bg-card p-4 transition-colors hover:bg-muted/50"
                  onClick={() => openUser(user)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openUser(user);
                    }
                  }}
                >
                  <UserIdentity user={user} />
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <UserRoleBadge role={user.role} />
                    <AccountStatus user={user} />
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="hidden rounded-md border md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <EmptyUsers
                        searchTerm={searchTerm}
                        loading={directoryQuery.isPending}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow
                      key={user.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => openUser(user)}
                    >
                      <TableCell className="font-medium">
                        {user.firstname} {user.lastname}
                      </TableCell>
                      <TableCell className="max-w-[240px] truncate">
                        {user.email}
                      </TableCell>
                      <TableCell>
                        <UserRoleBadge role={user.role} />
                      </TableCell>
                      <TableCell>
                        <AccountStatus user={user} />
                      </TableCell>
                      <TableCell>
                        {formatDate(user.accountcreationdate)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3">
            <Button
              variant="outline"
              onClick={() => setPageIndex((value) => Math.max(0, value - 1))}
              disabled={pageIndex === 0 || directoryQuery.isFetching}
            >
              Previous
            </Button>
            <div className="text-sm text-muted-foreground">
              Page {pageIndex + 1}
            </div>
            <Button
              variant="outline"
              onClick={() => {
                if (!nextCursor) return;
                setCursorHistory((history) => {
                  const next = history.slice(0, pageIndex + 1);
                  next[pageIndex + 1] = nextCursor;
                  return next;
                });
                setPageIndex((value) => value + 1);
              }}
              disabled={!nextCursor || directoryQuery.isFetching}
            >
              Next
            </Button>
          </div>
        </CardContent>
      </Card>

      <CreateUserDialog
        open={showCreatePopup}
        onOpenChange={(open) => {
          if (createMutation.isPending) return;
          setShowCreatePopup(open);
          if (!open) setFormData(emptyCreateForm());
        }}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleCreateSubmit}
        isLoading={createMutation.isPending}
        error={showCreatePopup ? errorMessage : undefined}
      />
      <Dialog open={Boolean(createdUser)} onOpenChange={(open) => { if (!open) setCreatedUser(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>User approved — awaiting sign-in</DialogTitle>
            <DialogDescription>Copy the email, temporary password and sign-in instructions below. They’ll set their own password on first login. Google sign-in remains available for the approved email.</DialogDescription>
          </DialogHeader>
          {createdUser && <InvitationNotice invitation={createdUser.invitation} />}
          <DialogFooter>
            <Button onClick={() => {
              if (!createdUser) return;
              navigate(`/users/${createdUser.id}`);
              setCreatedUser(null);
            }}>Open user</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const MessageBanner = ({
  error,
  success,
  onDismiss,
}: {
  error?: string;
  success: string;
  onDismiss: () => void;
}) => (
  <div
    role={error ? "alert" : "status"}
    className={`rounded-md border px-4 py-3 text-sm ${
      error
        ? "border-destructive/30 bg-destructive/10 text-destructive"
        : "border-emerald-200 bg-emerald-50 text-emerald-800"
    }`}
  >
    {error || success}
    <button
      type="button"
      className="ml-3 font-medium underline"
      onClick={onDismiss}
    >
      Dismiss
    </button>
  </div>
);

const UserIdentity = ({ user }: { user: UserDirectoryItem }) => (
  <div className="min-w-0">
    <div className="truncate text-sm font-semibold">
      {user.firstname} {user.lastname}
    </div>
    <div className="truncate text-xs text-muted-foreground">{user.email}</div>
  </div>
);

const EmptyUsers = ({
  searchTerm,
  loading,
}: {
  searchTerm: string;
  loading: boolean;
}) => (
  <div className="rounded-md border border-dashed p-8 text-center">
    <Search className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
    <div className="font-medium">
      {loading ? "Loading users..." : "No users found"}
    </div>
    {!loading && (
      <div className="text-sm text-muted-foreground">
        {searchTerm
          ? `No results for "${searchTerm}".`
          : "No users match the selected filters."}
      </div>
    )}
  </div>
);

const CreateUserDialog = ({
  open,
  onOpenChange,
  formData,
  setFormData,
  onSubmit,
  isLoading,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formData: CreateForm;
  setFormData: React.Dispatch<React.SetStateAction<CreateForm>>;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  error?: string;
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>Invite user</DialogTitle>
        <DialogDescription>
          Enter their sign-in email and choose their portal role. This approves them for Google sign-in. Owner includes Admin access and manages Xero; only an Owner can grant it after the first Owner is designated.
          We’ll also email an invitation when delivery is available, or give you a link for email/password or Microsoft setup.
        </DialogDescription>
      </DialogHeader>
      {error && (
        <div className="text-sm text-destructive" role="alert">
          {error}
        </div>
      )}
      <form className="space-y-4" onSubmit={onSubmit}>
        <div className="grid gap-4 md:grid-cols-2">
          <FormInput
            label="First name"
            name="firstName"
            value={formData.firstName}
            onChange={(event) =>
              setFormData((value) => ({
                ...value,
                firstName: event.target.value,
              }))
            }
          />
          <FormInput
            label="Last name"
            name="lastName"
            value={formData.lastName}
            onChange={(event) =>
              setFormData((value) => ({
                ...value,
                lastName: event.target.value,
              }))
            }
          />
        </div>
        <FormInput
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={(event) =>
            setFormData((value) => ({ ...value, email: event.target.value }))
          }
        />
        <div className="space-y-2">
          <Label>Contact number (optional)</Label>
          <PhoneInput
            country="au"
            value={formData.contactNumber}
            onChange={(value, _country, _event, formattedValue) =>
              setFormData((current) => ({
                ...current,
                contactNumber: formattedValue || value,
              }))
            }
            alwaysDefaultMask={false}
            inputProps={{ name: "phone" }}
            containerClass="!w-full"
            inputClass="!h-10 !w-full !rounded-md !border !border-input !bg-background !pl-12 !text-sm"
            buttonClass="!rounded-l-md !border-input !bg-background"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="create_timezone">Time zone</Label>
          <TimezoneSelect
            id="create_timezone"
            name="timezone"
            value={formData.timezone}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
              setFormData((value) => ({
                ...value,
                timezone: event.target.value,
              }))
            }
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="create-role">Role</Label>
          <Select
            value={formData.role}
            onValueChange={(role: UserRole) =>
              setFormData((value) => ({ ...value, role }))
            }
          >
            <SelectTrigger id="create-role">
              <SelectValue placeholder="Choose role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Client">Client</SelectItem>
              <SelectItem value="Agent">Agent</SelectItem>
              <SelectItem value="Owner">Owner</SelectItem>
              <SelectItem value="Admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Creating..." : "Create and invite"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);

const FormInput = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  readOnly = false,
}: {
  label: string;
  name: string;
  value: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  readOnly?: boolean;
}) => (
  <div className="space-y-2">
    <Label htmlFor={name}>{label}</Label>
    <Input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      readOnly={readOnly}
      required={!readOnly}
    />
  </div>
);

function formatDate(dateString: string | null) {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleString("en-AU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default Users;
